import React, { useState, useMemo } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { GeckoToastService, GeckoBtn, Tooltip, OverlayTrigger } from 'ui';
import Filter from '../../Filter';
import GeckoLocation from 'services/gecko-location';
import { removeFiltersFromQueryStringObject } from '../../../services/querystring-filters';
import {
  deleteSavedCustomFilter,
  pinSavedCustomFilter,
} from '../../../actions';
import { CONSTS } from 'constants/index.js';
import {
  getSavedCustomFilters,
  getSavedCustomFiltersPinLimitReached,
} from 'selectors';
import thumbTackCloseImg from 'assets/thumbtack-close.svg';

const CustomFilterDropdown = ({
  currentFilter,
  hasNotification,
  conversationLengths,
  onCustomFilterTabSelect,
  customFilterConversations,
  openFilterModal,
  onRemoveCustomFilter,
  onCustomFiltersChanged,
}) => {
  const [customFilterDropDownOpen, setCustomFilterDropDownOpen] =
    useState(false);
  const [customFilterSearch, setCustomFilterSearch] = useState('');
  const [hoveredFilter, setHoveredFilter] = useState(null);
  const savedCustomFilters = useSelector(getSavedCustomFilters);
  const pinDisabled = useSelector(getSavedCustomFiltersPinLimitReached);
  const filteredSavedCustomFilters = useMemo(
    () =>
      savedCustomFilters.filter((savedCustomFilter) =>
        savedCustomFilter.name
          .toLowerCase()
          .includes(customFilterSearch.toLowerCase())
      ),
    [savedCustomFilters, customFilterSearch]
  );
  const hasFilters = Boolean(customFilterConversations.filters.length);

  const handleCustomFilterDropdownClick = () => {
    if (currentFilter === CONSTS.FILTER_CUSTOM_FILTER || !hasFilters) {
      setCustomFilterDropDownOpen(!customFilterDropDownOpen);
    } else {
      onCustomFilterTabSelect();
    }
  };

  const handleCustomFilterSearchChange = (event) =>
    setCustomFilterSearch(event.target.value);

  const handleEditFilter = (existingSavedFilter) => {
    openFilterModal({
      existingSavedFilter: {
        ...existingSavedFilter,
        removeCustomFilter: onRemoveCustomFilter,
      },
    });
  };

  const handleNewFilter = () => openFilterModal({ noFilters: true });

  const handleDeleteFilter = async (filterId) => {
    try {
      await deleteSavedCustomFilter(filterId);
      onRemoveCustomFilter(filterId);
      new GeckoToastService().success('Custom filter deleted successfully');
    } catch (error) {
      new GeckoToastService().error();
    }
  };

  const handleUnTagClick = (filterId) => {
    onRemoveCustomFilter(filterId);
  };
  const handleTagClick = async (id) => {
    pinSavedCustomFilter(id);
  };

  return (
    <Dropdown
      className="CustomFilterDropdown"
      onToggle={handleCustomFilterDropdownClick}
      show={customFilterDropDownOpen}
    >
      <Dropdown.Toggle
        as={Filter}
        filter={CONSTS.FILTER_CUSTOM_FILTER}
        isActive={currentFilter === CONSTS.FILTER_CUSTOM_FILTER}
        hasUnreads={hasNotification[CONSTS.FILTER_CUSTOM_FILTER]}
        length={conversationLengths[CONSTS.FILTER_CUSTOM_FILTER]}
        onFilterSelected={handleCustomFilterDropdownClick}
        onFilterClear={
          currentFilter === CONSTS.FILTER_CUSTOM_FILTER &&
          customFilterConversations.filters.length
            ? () => {
                const queryString = GeckoLocation.params;
                removeFiltersFromQueryStringObject(queryString);
                GeckoLocation.goTo(
                  GeckoLocation.location.pathname,
                  queryString
                );
              }
            : null
        }
      />
      {/* local style required while migrating components, look to remove when tackling the direct GeckoDropdown/ core style usages */}
      <Dropdown.Menu className="gecko-dropdown-menu" style={{ top: '40px' }}>
        <div className="dropdown-item dropdown-search">
          <i className="far fa-search" />
          <input
            placeholder="Search for a saved filter"
            value={customFilterSearch}
            onChange={handleCustomFilterSearchChange}
          />
        </div>
        <Dropdown.Divider />
        {filteredSavedCustomFilters.length ? (
          filteredSavedCustomFilters.map((savedCustomFilter, index) => {
            const { id, name, filters } = savedCustomFilter;
            const isActive = savedCustomFilters.find(
              (customFilter) => customFilter.id === id
            ).pinned;
            const tagButtonDisabled = pinDisabled && !isActive;
            const isHovered = hoveredFilter?.id === id;
            return (
              <div
                key={index}
                className={classNames('dropdown-item', 'dropdown-filter', {
                  active: isActive,
                })}
                onMouseEnter={() => setHoveredFilter(savedCustomFilter)}
                onMouseLeave={() => setHoveredFilter(null)}
              >
                <button
                  className="filter-name-button"
                  onClick={() => {
                    onCustomFiltersChanged(filters);
                  }}
                >
                  {name}
                </button>
                <div>
                  {isHovered && (
                    <div className="filter-options">
                      <button
                        className="gecko-btn btn btn-sm btn-naked-primary icon-only"
                        onClick={() => handleEditFilter(savedCustomFilter)}
                      >
                        <i className="far fa-edit" />
                      </button>
                      <button
                        className={`pin-button gecko-btn btn btn-sm btn-naked-primary icon-only ${
                          tagButtonDisabled ? 'disabled' : ''
                        }`}
                        onClick={() => {
                          if (isActive) {
                            return handleUnTagClick(id);
                          }
                          handleTagClick(id);
                        }}
                        disabled={tagButtonDisabled}
                      >
                        {isActive ? (
                          <img src={thumbTackCloseImg} />
                        ) : (
                          <>
                            {tagButtonDisabled ? (
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Tooltip id="pin-icon">
                                    You already have 2 pinned custom filters.
                                    Please remove one to be able to pin more.
                                  </Tooltip>
                                }
                              >
                                <i className="far fa-thumbtack" id="pin-icon" />
                              </OverlayTrigger>
                            ) : (
                              <i className="far fa-thumbtack" id="pin-icon" />
                            )}
                          </>
                        )}
                      </button>
                      <button
                        className="gecko-btn btn btn-sm btn-naked-danger icon-only"
                        onClick={() => handleDeleteFilter(id)}
                      >
                        <i className="far fa-trash-alt" />
                      </button>
                    </div>
                  )}
                  {isActive && !isHovered && <i className="far fa-check" />}
                </div>
              </div>
            );
          })
        ) : (
          <div className="dropdown-item dropdown-message">No filters found</div>
        )}
        <Dropdown.Divider />
        <div className="dropdown-options">
          {hasFilters && (
            <GeckoBtn
              name="Edit Filter"
              type="default"
              handler={onCustomFilterTabSelect}
              icon="far fa-edit"
              size="sm"
            />
          )}
          <GeckoBtn
            name="New Filter"
            type="primary"
            handler={handleNewFilter}
            icon="far fa-sliders-h"
            size="sm"
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CustomFilterDropdown;
